package dev.lingvo.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.TransformOrigin
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.content
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.transformOrigin
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.modifiers.zIndex
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.ProjectAccentKey
import dev.lingvo.shared.ProjectIconKey
import dev.lingvo.shared.Role
import dev.lingvo.shared.atLeast
import dev.lingvo.site.api.Session
import dev.lingvo.site.ds.HIT_AREA_FLOOR_PX
import dev.lingvo.site.ds.IconSize
import dev.lingvo.site.ds.Motion
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.Transparent
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.iconSize
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.ds.transitionColors
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.nav.AppRoute
import dev.lingvo.site.nav.AppScope
import dev.lingvo.site.state.ProjectDirectory
import dev.lingvo.site.state.ProjectRouteContext
import dev.lingvo.site.state.WorkspaceRequestContext
import dev.lingvo.site.state.WorkspaceSelection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Nav
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * Where you are, and the only way to go somewhere comparable.
 *
 * **This replaces two dropdowns in the sidebar and a breadcrumb that printed URL segments.** The
 * project switcher was the most prominent control in the app and applied to sixteen of fifty-seven
 * routes — on the rest it read "Select a project", a permanently empty form field. It was drawn as
 * a peer of the workspace switcher although a project is strictly inside a workspace. And it was a
 * `<select>` that navigated on change, which commits a destination as you arrow past it: a WCAG
 * 3.2.2 change-of-context failure that also removed middle-click, open-in-new-tab, and the link
 * lists screen readers build.
 *
 * The trail states the containment instead of hiding it — `Acme › Storefront › Strings` — and every
 * option inside every picker is a real `<a href>`, so choosing one is an ordinary link activation.
 */
@Composable
fun AppTrail(currentPath: String) {
    val scope = rememberCoroutineScope()
    val slug = AppRoute.projectSlug(currentPath)
    val verifiedRoute = slug?.let(ProjectRouteContext::current)
    val requestContext = verifiedRoute?.workspace ?: WorkspaceRequestContext.current()
    val workspace = requestContext?.workspace
    requestContext?.let { ProjectDirectory.ensure(scope, it) }

    Nav(
        AppTrailStyle.toModifier().toAttrs { attr("aria-label", tr("app_trail.location", "Location")) },
    ) {
        when (AppRoute.scopeOf(currentPath)) {
            AppScope.USER -> {
                Crumb(t("app_trail.you", "You"), "/app/account")
            }
            AppScope.WORKSPACE, AppScope.PROJECT -> {
                // The slug travels with the name because the tick has to mark what this tab is
                // *rendering*. See [WorkspaceCrumb].
                WorkspaceCrumb(workspace?.name ?: t("app_trail.workspace", "Workspace"), workspace?.slug)
            }
        }

        if (slug != null) {
            Separator()
            // The project's *name*. The breadcrumb printed the slug, which is the one thing on this
            // row somebody cannot be expected to recognise at a glance.
            val verifiedProject = verifiedRoute?.project
            val directoryEntry = ProjectDirectory.find(slug)
            val name = verifiedProject?.name ?: directoryEntry?.name ?: slug
            ProjectCrumb(
                name = name,
                slug = slug,
                currentPath = currentPath,
                lang = verifiedProject?.sourceLocale ?: directoryEntry?.sourceLocale,
                iconKey = verifiedProject?.iconKey ?: directoryEntry?.iconKey ?: ProjectIconKey.MONOGRAM,
                accentKey = verifiedProject?.accentKey ?: directoryEntry?.accentKey ?: ProjectAccentKey.INK,
                hasCustomIcon = verifiedProject?.hasCustomIcon ?: directoryEntry?.hasCustomIcon ?: false,
            )
        }

        AppRoute.sectionLabel(currentPath)?.let { section ->
            Separator()
            Span(
                AppTrailCurrentStyle.toModifier().toAttrs { attr("aria-current", "page") },
            ) {
                Text(section)
            }
        }
    }
}

/**
 * @param renderedSlug the workspace this tab is actually drawing, which is **not**
 *   `WorkspaceSelection.selectedSlug`. `selectedSlug` is what local storage last recorded: null on a
 *   fresh browser, and the *other* tab's choice after a cross-tab switch. Ticking it meant the trail
 *   named one workspace while the list below it ticked nothing — or ticked a different one.
 */
@Composable
private fun WorkspaceCrumb(name: String, renderedSlug: String?) {
    val workspaces = Session.organizations
    // One workspace is not a choice. Drawing a disclosure for it is the same noise the old
    // switcher avoided by hiding itself, and the same reasoning applies here.
    if (workspaces.size < 2) {
        Crumb(name, "/app")
        return
    }
    var open by remember { mutableStateOf(false) }
    Picker(
        label = name,
        // A Kotlin template is one expression to the compiler and no string at all to the
        // extractor, so this — the accessible name of the shell's most consequential control —
        // stayed English in every locale while the label beside it did not.
        ariaLabel = tf(
            "app_trail.switch_workspace_currently_name",
            "Switch workspace, currently {name}",
            "name" to name,
        ),
        idSeed = "workspace-${renderedSlug.orEmpty()}",
        open = open,
        onOpenChange = { open = it },
    ) {
        workspaces.forEach { candidate ->
            // Switching workspace goes home. Staying put would leave you on a page belonging to the
            // workspace you just left — the audit's "the URL stays, the data silently changes"
            // failure, which strands you on a detail route that then 404s.
            val current = candidate.slug == renderedSlug
            PickerRow(
                label = candidate.name,
                href = "/app",
                current = current,
                onActivate = { WorkspaceSelection.select(candidate.slug) },
                // **The one control in the shell whose effect leaves this tab.** The selection is
                // one value in local storage for the whole origin, so choosing here re-points every
                // other open Lingvo tab as well — and the product's answer to that was an apology
                // in those other tabs (`WorkspaceDriftBanner`) rather than a word in the control
                // causing it. `PickerRow` already had the slot; the project picker beside this one
                // uses it for something far less consequential.
                note = if (current) null else t("app_trail.switches_every_open_tab", "Switches every open tab"),
            )
        }
        PickerRow(t("app_trail.all_workspaces", "All workspaces"), "/app/workspaces", current = false, onActivate = {})
    }
}

@Composable
private fun ProjectCrumb(
    name: String,
    slug: String,
    currentPath: String,
    lang: String?,
    iconKey: ProjectIconKey,
    accentKey: ProjectAccentKey,
    hasCustomIcon: Boolean,
) {
    var open by remember { mutableStateOf(false) }
    val projects = ProjectDirectory.projects
    val carries = AppRoute.carriesSection(currentPath)
    // Creating a project is a management action, and the API refuses it below ADMIN. A `New project`
    // row offered to a translator staring at an empty picker is the one moment the product has to be
    // honest about what they can do next.
    val mayCreate = WorkspaceRequestContext.current()?.role?.atLeast(Role.ADMIN) == true
    Picker(
        label = name,
        labelLang = lang,
        ariaLabel = tf(
            "app_trail.switch_project_currently_name",
            "Switch project, currently {name}",
            "name" to name,
        ),
        idSeed = "project-$slug",
        open = open,
        onOpenChange = { open = it },
        // The project picker may grow to a 360px cap: its rows carry a second line, and 320 set them wrapping.
        width = PROJECT_PICKER_WIDTH,
        leading = {
            ProjectIcon(
                slug = slug,
                name = name,
                iconKey = iconKey,
                accentKey = accentKey,
                hasCustomIcon = hasCustomIcon,
                modifier = Modifier.width(24.px).minWidth(24.px).height(24.px),
            )
        },
    ) {
        if (projects.isEmpty()) {
            // Three different states, three different sentences. "No other projects yet" was shown
            // for all of them, so a failed request read as an empty workspace.
            PickerNote(
                when {
                    ProjectDirectory.loading -> t("app_trail.loading_your_projects", "Loading your projects…")
                    ProjectDirectory.error != null ->
                        t("app_trail.couldnt_load_your_projects", "Couldn't load your projects. Reload the page.")
                    // **Literal, and it is not "No *other* projects yet".** The workspace has none
                    // at all; saying "other" implies the one you are in exists somewhere in a list
                    // that is in fact empty.
                    else -> t("app_trail.no_projects_in_this_workspace_yet", "No projects in this workspace yet.")
                },
            )
            // Only under the *genuinely empty* sentence — never under "loading" or "couldn't
            // load", where offering to create a second project would be answering a question
            // nobody has yet asked.
            if (!ProjectDirectory.loading && ProjectDirectory.error == null) {
                if (mayCreate) {
                    PickerRow(t("app_trail.new_project", "New project"), "/app/projects/new", current = false, onActivate = {})
                } else {
                    // **The refusal says why.** Drawing nothing here was the shell's one silent
                    // refusal: a translator opened the picker on an empty workspace and got a
                    // sentence saying there is nothing, with no hint that creating one is a thing
                    // that exists and is not theirs to do. The comment above already argued for
                    // this and the code answered it with a blank.
                    PickerNote(
                        t(
                            "app_trail.only_an_owner_or_admin_can_create",
                            "Only an owner or admin can create a project here.",
                        ),
                    )
                }
            }
        }
        projects.forEach { project ->
            PickerRow(
                label = project.name,
                labelLang = project.sourceLocale,
                // Computed per row, so the destination is known before activation rather than
                // decided by a change event. Where the section travels, it travels.
                href = AppRoute.retarget(currentPath, project.slug),
                current = project.slug == slug,
                onActivate = {},
                iconKey = project.iconKey,
                accentKey = project.accentKey,
                slug = project.slug,
                hasCustomIcon = project.hasCustomIcon,
                // Says where it will land when that is not the same sub-page, instead of silently
                // dropping you on an overview.
                note = if (!carries && AppRoute.projectSection(currentPath) != null) {
                    t("app_trail.opens_the_overview", "Opens the overview")
                } else {
                    null
                },
            )
        }
        // The count in parentheses, and **no parenthesis at all when it is zero** — a zero is never
        // rendered inside a link, and `All projects (0)` invites a press that lands on an empty page.
        //
        // The count stays outside the catalogue entry: a translator gets the label, not a sentence
        // with a number glued into the middle of it.
        val allProjects = t("app_trail.all_projects", "All projects")
        val all = if (projects.isEmpty()) allProjects else "$allProjects (${projects.size})"
        PickerRow(all, "/app/projects", current = false, onActivate = {})
    }
}

@Composable
private fun Crumb(label: String, href: String) {
    Link(
        path = href,
        modifier = AppTrailCrumbStyle.toModifier(),
        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
    ) {
        Span(AppTrailTriggerLabelStyle.toModifier().toAttrs()) { Text(label) }
    }
}

@Composable
private fun Separator() {
    Span(AppTrailSeparatorStyle.toModifier().toAttrs { attr("aria-hidden", "true") }) { Text("/") }
}

val AppTrailStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("align-items", "center")
        // 0: the separator carries its own 12px margins, and a flex gap on top of them would put
        // the crumbs 14 apart while the spec measures 12.
        property("gap", "0")
        property("min-width", "0")
    }
}

val AppTrailCrumbStyle = CssStyle {
    base {
        Modifier
            .position(Position.Relative)
            .height(32.px)
            // Same reason as the trigger: shrink before the trail overflows into its neighbour.
            //
            // And shrink *first*. Flex distributes shrinkage by base size, so an equal share left
            // the project reading "Li…" beside a workspace still showing "Maya's wo…" — the trail
            // spending its last pixels on the crumb that matters least. An ancestor yields before
            // the thing you are actually looking at; the workspace name is also still whole in the
            // picker this crumb opens.
            .styleModifier {
                property("flex-shrink", "99")
                // 0, not a floor. A 40px floor here read better — a shortened name rather than a
                // bare "M" — and put the document into horizontal scroll at 900px, because the
                // trail could then no longer absorb what the bar could not fit. A crumb that looks
                // odd is worth less than a page that does not scroll sideways.
                property("min-width", "0")
            }
            .padding(leftRight = Space.SM)
            .borderRadius(Radius.Control)
            // 15, not 13.5. The trail is the sentence that says what the page acts on; at the shell's
            // smallest step it read as metadata about the page rather than as its address.
            .fontSize(TypeScale.BodySmall)
            .fontWeight(600)
            .color(colorMode.toSitePalette().textSecondary)
            .transitionColors(Motion.FastMs)
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
            }
    }
    // Visual 32, hit 40. At 4px padding on 13.5 type the crumb was roughly 28 tall — a target
    // eight pixels under the floor, in the one row of the app somebody clicks while moving.
    cssRule("::after") { hitAreaOverlay() }
    hover {
        Modifier
            .backgroundColor(colorMode.toSitePalette().surfaceSunken)
            .color(colorMode.toSitePalette().textPrimary)
    }
}

/**
 * The transparent strip that lifts a control under 40 tall up to the pointer floor.
 *
 * Stated once because three styles in this file need the identical geometry, and three copies of a
 * hit area is three chances for one of them to drift a pixel and stop abutting its neighbour.
 *
 * Internal rather than private since the top bar's `⌘K` chip needs the same lift (see
 * `AppCommandChordStyle` in `AppShellChrome.kt`) — a fourth copy of these six properties is a
 * fourth chance to drift.
 */
internal fun hitAreaOverlay(): Modifier = Modifier
    .content("")
    .position(Position.Absolute)
    .styleModifier {
        property("top", "50%")
        property("left", "0")
        property("width", "100%")
        property("height", "${HIT_AREA_FLOOR_PX}px")
        property("transform", "translateY(-50%)")
    }

/**
 * The trigger's inline padding, named because one caller has to cancel it exactly.
 *
 * A picker used as an eyebrow sits above an H1 that shares the content column's left edge, and this
 * padding is what put its label 8px right of that edge — two left edges in one header, the heavier
 * one wrong. `Picker(flushStart = true)` pulls the trigger back by this value and adds the same
 * value at the other end, so the two numbers cannot drift apart into a one-pixel misalignment
 * nobody can explain.
 *
 * Both ends, not one: everything between the trigger and the heading is shrink-to-fit, and a lone
 * negative margin comes out of the width each of those boxes resolves to — which the label, clipping
 * with an ellipsis because the trail needs it to, is what pays. See `Picker`'s `flushStart`.
 */
internal val AppTrailTriggerInlinePadding = Space.SM

val AppTrailTriggerStyle = CssStyle {
    base {
        Modifier
            .position(Position.Relative)
            .margin(0.px)
            .height(32.px)
            .padding(leftRight = AppTrailTriggerInlinePadding)
            .backgroundColor(Transparent)
            .border(0.px, LineStyle.None, Transparent)
            .borderRadius(Radius.Control)
            .fontFamily("inherit")
            .fontSize(TypeScale.BodySmall)
            .fontWeight(600)
            .color(colorMode.toSitePalette().textPrimary)
            .cursor(Cursor.Pointer)
            .transitionColors(Motion.FastMs)
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
                property("gap", "4px")
                // `min(220px, 100%)`, not `220px`. The trigger is an inline-flex, so its width is
                // shrink-to-fit over its min-content — and its label is `nowrap`, so min-content is
                // the whole project name. `min-width: 0` on the label lets it shrink once the button
                // has a size, but it does not stop the button sizing itself past its parent first:
                // measured at a 700px viewport the wrapper was 54px and the button 124px, spilling
                // the name across the search field. Capping at 100% of the containing block is what
                // makes the ellipsis below reachable.
                property("max-width", "min(220px, 100%)")
                // **Shrinkable, and clipped when it shrinks.** A flex item will not go below its
                // content width without this, so at a narrow viewport the trail kept its full
                // width, overflowed its own box and painted the project name across the search
                // field beside it. `overflow` is safe here: the picker's popover is a sibling of
                // this button inside the relative wrapper, not a child of it.
                property("min-width", "0")
            }
    }
    // Visual 32, hit 40 — the same floor the crumbs take, and for the same reason.
    cssRule("::after") { hitAreaOverlay() }
    hover { Modifier.backgroundColor(colorMode.toSitePalette().surfaceSunken) }
    focusVisible {
        Modifier.styleModifier {
            property("outline", "2px solid ${colorMode.toSitePalette().accent}")
            property("outline-offset", "2px")
        }
    }
}

val AppTrailChevronStyle = CssStyle.base {
    Modifier
        .iconSize(IconSize.SM)
        .color(colorMode.toSitePalette().textSecondary)
}

val AppTrailSeparatorStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().textMuted)
        .fontSize(TypeScale.BodySmall)
        // 12 each side. At the trail's 2px flex gap the slash sat against both crumbs and the row
        // read as one hyphenated string rather than as three things containing each other.
        .margin(leftRight = Space.MD)
}

val AppTrailCurrentStyle = CssStyle.base {
    Modifier
        .padding(leftRight = Space.SM)
        .fontSize(TypeScale.BodySmall)
        .fontWeight(600)
        .color(colorMode.toSitePalette().textPrimary)
        .styleModifier {
            property("white-space", "nowrap")
            property("overflow", "hidden")
            property("text-overflow", "ellipsis")
            property("max-width", "260px")
        }
}

/**
 * The truncating text inside a crumb or the trail's trigger.
 *
 * The button is an inline-flex holding this and the chevron, and `text-overflow` does nothing on a
 * flex container — it has to sit on the text's own box. Without it the name pushed the button past
 * its container and over the search field at narrow widths.
 *
 * **The clipping lives here and not on the control**, which is the whole reason this style exists
 * rather than three extra properties on the crumb and the trigger. Both of those carry an `::after`
 * hit-area overlay 40px tall on a 32px control, and both are its containing block, so `overflow:
 * hidden` on them clips the overlay back to the visible 32 — turning a fix for an overlap into a
 * regression of every pointer target in the trail. Measured: with the control clipped, a click 3px
 * above it landed on the top bar instead.
 */
val AppTrailTriggerLabelStyle = CssStyle.base {
    Modifier.styleModifier {
        property("min-width", "0")
        property("overflow", "hidden")
        property("text-overflow", "ellipsis")
        property("white-space", "nowrap")
    }
}

val AppTrailPopoverStyle = CssStyle {
    base {
        Modifier
            .position(Position.Absolute)
            .zIndex(AppLayer.PickerPopover)
            .margin(top = Space.XXS)
            .padding(Space.XXS)
            .backgroundColor(colorMode.toSitePalette().surface)
            // A floating surface takes the strong stroke; `border` is the in-flow divider.
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
            .borderRadius(Radius.Card)
            .transformOrigin(TransformOrigin.TopLeft)
            .styleModifier {
                property("inline-size", "max-content")
                // A floor wider than the trigger: a short list ("German", "French", "Spanish")
                // otherwise sized the popover to the trigger's own width — 110px measured on the
                // editor's locale picker — leaving every stretched row that narrow too.
                property("min-inline-size", "max(100%, 200px)")
                property("max-inline-size", "min(var(--picker-max-width), calc(100vw - 32px))")
                property("max-height", "60vh")
                property("overflow-x", "hidden")
                property("overflow-y", "auto")
                property(
                    "transition",
                    "opacity ${Motion.StandardMs}ms ${Motion.EaseEntrance}, " +
                        "transform ${Motion.StandardMs}ms ${Motion.EaseEntrance}",
                )
            }
    }
    cssRule("[data-presence=\"entering\"]") {
        Modifier.styleModifier {
            property("transform", "translateY(-${Motion.PopoverTravelPx}px) scale(${Motion.PopoverEnterScale})")
        }
    }
    cssRule("[data-presence=\"exiting\"]") {
        Modifier.styleModifier {
            property("opacity", "0")
            property("transform", "translateY(-${Motion.PopoverTravelPx}px) scale(${Motion.PopoverEnterScale})")
            property(
                "transition",
                "opacity ${Motion.FastMs}ms ${Motion.EaseIn}, transform ${Motion.FastMs}ms ${Motion.EaseIn}",
            )
            property("pointer-events", "none")
        }
    }
}

val AppTrailRowStyle = CssStyle {
    base {
        Modifier
            .padding(topBottom = Space.XS, leftRight = Space.SM)
            .borderRadius(Radius.Control)
            .fontSize(TypeScale.Small)
            .transitionColors(Motion.FastMs)
            .styleModifier {
                property("display", "block")
                // The popover's `Column` is a flex container with `align-items: flex-start`, so
                // without this every row shrank to its own label's width instead of the popover's —
                // measured at 84-100px wide inside a 110px popover, each with dead space at its
                // trailing edge that a hover or a click did not light up.
                property("width", "100%")
                property("box-sizing", "border-box")
            }
    }
    hover { Modifier.backgroundColor(colorMode.toSitePalette().surfaceSunken) }
    focusVisible {
        Modifier.styleModifier {
            property("outline", "2px solid ${colorMode.toSitePalette().accent}")
            property("outline-offset", "-2px")
        }
    }
}


/** Two lines per row, so it needs the extra 40. */
private const val PROJECT_PICKER_WIDTH = 360
